import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { supabase } from "@/integrations/supabase/client";
import {
  staticReviews,
  GOOGLE_RATING,
  GOOGLE_REVIEW_COUNT,
  GOOGLE_MAPS_URI,
} from "@/data/reviews";
import { useLanguage } from "@/contexts/LanguageContext";


interface Review {
  rating: number;
  text: string;
  relativeTime: string;
  publishTime: string;
  author: {
    name: string;
    photo: string;
    profileUri: string;
  };
  reviewUri: string;
}

interface ReviewsData {
  placeName: string;
  rating: number | null;
  userRatingCount: number;
  googleMapsUri: string;
  reviews: Review[];
}

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Stars = ({ rating }: { rating: number }) => (
  <div className="flex gap-0.5" aria-label={`${rating} van 5 sterren`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.round(rating)
            ? "fill-primary text-primary"
            : "text-muted-foreground/30"
        }`}
      />
    ))}
  </div>
);

// Optionele functietitels/bedrijfsnamen per reviewer (authority bias).
// Vul aan zodra we toestemming/gegevens hebben van de klant.
const REVIEWER_META: Record<string, string> = {};

const FEATURED_QUOTE = {
  text: "Never laughed so hard at an office party!",
  author: "Google review",
};

const STATIC_DATA: ReviewsData = {
  placeName: "PlayfieldVR",
  rating: GOOGLE_RATING,
  userRatingCount: GOOGLE_REVIEW_COUNT,
  googleMapsUri: GOOGLE_MAPS_URI,
  reviews: staticReviews,
};

const GoogleReviews = () => {
  const { language } = useLanguage();
  // Start met statische reviews: deze staan zo direct in de HTML/eerste render,
  // ook zonder JavaScript. De live API verrijkt de data daarna alleen.
  const [data, setData] = useState<ReviewsData>(STATIC_DATA);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: resp, error: fnError } = await supabase.functions.invoke(
          "google-reviews",
          { method: "GET" }
        );
        if (fnError) throw fnError;
        const payload = resp as ReviewsData;
        if (!active || !payload) return;
        // De statische reviewlijst blijft de bron van waarheid (staat in de
        // server-HTML én in de JSON-LD). De live API verrijkt alleen de
        // bestaande reviews (foto, datum, bronlink) en het totaalcijfer,
        // zodat HTML, carousel en structured data exact overeenkomen.
        const live = payload.reviews ?? [];
        setData({
          ...STATIC_DATA,
          rating: payload.rating ?? STATIC_DATA.rating,
          userRatingCount: payload.userRatingCount || STATIC_DATA.userRatingCount,
          googleMapsUri: payload.googleMapsUri || STATIC_DATA.googleMapsUri,
          reviews: STATIC_DATA.reviews.map((review) => {
            const match = live.find((r) => r.author?.name === review.author.name);
            return match ? { ...review, ...match, text: review.text } : review;
          }),
        });
      } catch (e) {
        console.error("Failed to load Google reviews", e);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const t = {
    title: language === "nl" ? "Wat klanten zeggen" : "What customers say",
    subtitle:
      language === "nl"
        ? "Echte reviews van onze Google Business pagina"
        : "Real reviews from our Google Business page",
    viewAll: language === "nl" ? "Bekijk alle reviews op Google" : "View all reviews on Google",
    based: language === "nl" ? "Gebaseerd op" : "Based on",
    reviewsWord: language === "nl" ? "reviews" : "reviews",
  };


  return (
    <section id="reviews" className="py-14 sm:py-24 bg-card/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-card border border-border mb-4">
            <GoogleLogo />
            <span className="text-sm font-medium text-foreground">
              Google Reviews
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">
            <span className="text-primary">{t.title}</span>
          </h2>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Stars rating={data.rating ?? 5} />
            <span className="text-lg font-bold text-foreground">
              {data.rating?.toFixed(1) ?? "5.0"}
            </span>
            <span className="text-sm text-muted-foreground">
              · {t.based} {data.userRatingCount} {t.reviewsWord}
            </span>
          </div>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base">
            {t.subtitle}
          </p>
        </div>

        {/* Featured quote */}
        <figure className="max-w-3xl mx-auto mb-8 sm:mb-14 rounded-2xl border border-primary/40 bg-card p-6 sm:p-10 text-center">
          <div className="flex justify-center mb-4">
            <Stars rating={5} />
          </div>
          <blockquote className="text-xl sm:text-2xl md:text-3xl font-black leading-snug text-foreground">
            &ldquo;{FEATURED_QUOTE.text}&rdquo;
          </blockquote>
          <figcaption className="mt-4 text-sm text-muted-foreground flex items-center justify-center gap-2">
            <GoogleLogo />
            <span>{FEATURED_QUOTE.author}</span>
          </figcaption>
        </figure>

        {/* Carousel */}
        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full max-w-6xl mx-auto"
        >
          <CarouselContent className="-ml-4">
            {data.reviews.map((review, idx) => (
              <CarouselItem
                key={idx}
                className="pl-4 md:basis-1/2 lg:basis-1/3"
              >
                <article className="h-full bg-card border border-border rounded-xl p-6 flex flex-col hover:border-primary/50 transition-colors">
                  {/* Author */}
                  <div className="flex items-center gap-3 mb-4">
                    {review.author.photo ? (
                      <img
                        src={review.author.photo}
                        alt={review.author.name}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="w-11 h-11 rounded-full object-cover border border-border"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                        {review.author.name.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate">
                        {review.author.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {REVIEWER_META[review.author.name]
                          ? REVIEWER_META[review.author.name]
                          : review.relativeTime}
                      </p>
                    </div>
                    <GoogleLogo />
                  </div>

                  {/* Stars */}
                  <div className="mb-3">
                    <Stars rating={review.rating} />
                  </div>

                  {/* Text */}
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed flex-1">
                    {review.text}
                  </p>

                  {/* Source link (attribution) */}
                  {review.reviewUri && (
                    <a
                      href={review.reviewUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      {language === "nl" ? "Bron: Google" : "Source: Google"}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </article>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden sm:flex -left-4 lg:-left-12 bg-card border-border hover:bg-primary hover:text-primary-foreground" />
          <CarouselNext className="hidden sm:flex -right-4 lg:-right-12 bg-card border-border hover:bg-primary hover:text-primary-foreground" />
        </Carousel>

        {/* View all link */}
        <div className="text-center mt-10">
          <a
            href={data.googleMapsUri}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-card border border-border hover:border-primary transition-colors group"
          >
            <GoogleLogo />
            <span className="font-medium text-foreground group-hover:text-primary transition-colors">
              {t.viewAll}
            </span>
            <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
